import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
  Send, Mic, MicOff, Paperclip, Image as ImageIcon, Link2, X, AtSign, Hash, Video, FileText
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { DEPT_NAMES, getYouTubeId } from './slateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MessageComposer({ channelId, projectId, token, onSent, replyTo, onCancelReply, projectUsers }) {
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [acMode, setAcMode] = useState(null);
  const [acItems, setAcItems] = useState([]);
  const [acIdx, setAcIdx] = useState(0);
  const textareaRef = useRef();
  const fileInputRef = useRef();
  const imageInputRef = useRef();
  const mediaRecorderRef = useRef();
  const chunksRef = useRef([]);
  const typingTimerRef = useRef();
  const headers = { Authorization: `Bearer ${token}` };

  const handleChange = (val) => {
    setContent(val);
    const cur = textareaRef.current?.selectionStart ?? val.length;
    const before = val.slice(0, cur);
    const dm = before.match(/#(\w*)$/);
    const um = before.match(/@([\w\s]*)$/);
    if (dm) {
      setAcMode('#');
      setAcItems(DEPT_NAMES.filter(d => d.startsWith(dm[1].toLowerCase())));
      setAcIdx(0);
    } else if (um) {
      setAcMode('@');
      setAcItems(projectUsers.filter(u => u.full_name.toLowerCase().includes(um[1].toLowerCase())));
      setAcIdx(0);
    } else {
      setAcMode(null);
    }
    clearTimeout(typingTimerRef.current);
    if (val.trim()) {
      typingTimerRef.current = setTimeout(() => {
        axios.post(`${API}/projects/${projectId}/slate/channels/${channelId}/typing`, {}, { headers }).catch(() => {});
      }, 400);
    }
  };

  const insertAc = (item) => {
    const cur = textareaRef.current?.selectionStart ?? content.length;
    const before = content.slice(0, cur);
    const after = content.slice(cur);
    const name = typeof item === 'string' ? item : item.full_name;
    const newBefore = acMode === '#'
      ? before.replace(/#(\w*)$/, `#${name} `)
      : before.replace(/@([\w\s]*)$/, `@${name} `);
    setContent(newBefore + after);
    setAcMode(null);
    setTimeout(() => textareaRef.current?.focus(), 30);
  };

  const handleKeyDown = (e) => {
    if (acMode && acItems.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setAcIdx(i => Math.min(i + 1, acItems.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setAcIdx(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertAc(acItems[acIdx]); return; }
      if (e.key === 'Escape') { setAcMode(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey && !acMode) { e.preventDefault(); handleSend(); }
  };

  const handleSend = async () => {
    if (!content.trim() && attachments.length === 0) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/projects/${projectId}/slate/channels/${channelId}/messages`,
        { content: content.trim(), attachments, thread_parent_id: replyTo?.id || null },
        { headers }
      );
      setContent(''); setAttachments([]);
      if (replyTo) onCancelReply?.();
      onSent?.();
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/projects/${projectId}/slate/upload`, fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setAttachments(prev => [...prev, res.data]);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const addLink = () => {
    if (!linkInput.trim()) return;
    const ytId = getYouTubeId(linkInput);
    setAttachments(prev => [...prev, {
      id: `link-${Date.now()}`, type: ytId ? 'video_link' : 'link',
      url: linkInput.trim(), filename: linkInput.trim(),
    }]);
    setLinkInput(''); setShowLinkInput(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
        await uploadFile(file);
      };
      mr.start(); mediaRecorderRef.current = mr; setRecording(true);
    } catch { toast.error('Microphone access denied'); }
  };

  const stopRecording = () => { mediaRecorderRef.current?.stop(); setRecording(false); };

  const AttachIcon = ({ a }) => {
    if (a.type === 'image') return <ImageIcon className="h-3.5 w-3.5 text-[#e8c547]" />;
    if (a.type === 'voice') return <Mic className="h-3.5 w-3.5 text-[#e8c547]" />;
    if (a.type === 'video_link') return <Video className="h-3.5 w-3.5 text-red-400" />;
    if (a.type === 'link') return <Link2 className="h-3.5 w-3.5 text-blue-400" />;
    return <FileText className="h-3.5 w-3.5 text-[#a0a0a0]" />;
  };

  return (
    <div className="border-t border-[#2a2a2a] px-4 py-3 shrink-0">
      {replyTo && (
        <div className="flex items-center justify-between bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-3 py-1.5 mb-2">
          <div className="text-xs text-[#a0a0a0]">
            Replying to <span className="text-[#e8c547]">{replyTo.user_name}</span>
            {': '}<span className="text-[#555]">{replyTo.content?.slice(0, 60)}</span>
          </div>
          <button onClick={onCancelReply} className="text-[#555] hover:text-[#f0f0f0]"><X className="h-3 w-3" /></button>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {attachments.map(a => (
            <div key={a.id} className="flex items-center gap-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-3 py-1.5">
              <AttachIcon a={a} />
              <span className="text-xs text-[#a0a0a0] max-w-[100px] truncate">{a.filename}</span>
              <button onClick={() => setAttachments(prev => prev.filter(x => x.id !== a.id))} className="text-[#555] hover:text-red-400"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}

      {showLinkInput && (
        <div className="flex gap-2 mb-2">
          <Input value={linkInput} onChange={e => setLinkInput(e.target.value)}
            placeholder="YouTube, Vimeo, or any URL..." autoFocus
            className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] text-sm h-8 flex-1"
            onKeyDown={e => e.key === 'Enter' && addLink()} />
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-3 text-xs" onClick={addLink}>Add</Button>
          <button onClick={() => setShowLinkInput(false)} className="text-[#555] hover:text-[#f0f0f0]"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {acMode && acItems.length > 0 && (
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg overflow-hidden mb-2 shadow-lg">
          {acItems.slice(0, 6).map((item, i) => (
            <button key={i} onClick={() => insertAc(item)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#2a2a2a] transition-colors ${i === acIdx ? 'bg-[#2a2a2a]' : ''}`}>
              {acMode === '#'
                ? <><Hash className="h-3.5 w-3.5 text-[#e8c547]" /><span>{item}</span></>
                : <><AtSign className="h-3.5 w-3.5 text-blue-400" /><span>{item.full_name}</span><span className="text-xs text-[#555]">{item.department}</span></>}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl px-3 py-2 focus-within:border-[#e8c547]/40 transition-colors">
        <div className="flex items-center gap-1 pb-0.5">
          <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden"
            onChange={e => Array.from(e.target.files || []).forEach(f => uploadFile(f))} />
          <button onClick={() => imageInputRef.current?.click()} className="h-7 w-7 rounded flex items-center justify-center text-[#555] hover:text-[#e8c547] transition-colors" title="Images" disabled={uploading}>
            <ImageIcon className="h-4 w-4" />
          </button>
          <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.txt,.xlsx,.pptx" multiple className="hidden"
            onChange={e => Array.from(e.target.files || []).forEach(f => uploadFile(f))} />
          <button onClick={() => fileInputRef.current?.click()} className="h-7 w-7 rounded flex items-center justify-center text-[#555] hover:text-[#a0a0a0] transition-colors" title="Files" disabled={uploading}>
            <Paperclip className="h-4 w-4" />
          </button>
          <button onClick={() => setShowLinkInput(v => !v)} className="h-7 w-7 rounded flex items-center justify-center text-[#555] hover:text-blue-400 transition-colors" title="Add link">
            <Link2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => { if (recording) { stopRecording(); } else { startRecording(); } }}
            className={`h-7 w-7 rounded flex items-center justify-center transition-colors ${recording ? 'text-red-500 bg-red-500/20 animate-pulse' : 'text-[#555] hover:text-red-400'}`}
            title={recording ? 'Stop recording' : 'Record voice note'}>
            {recording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </button>
        </div>
        <Textarea ref={textareaRef} value={content} onChange={e => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message... (# for dept, @ for person)"
          className="flex-1 bg-transparent border-0 text-[#f0f0f0] text-sm resize-none focus-visible:ring-0 focus-visible:ring-offset-0 min-h-[36px] max-h-32 p-0"
          rows={1} data-testid="slate-input" />
        <Button size="sm" onClick={handleSend}
          disabled={sending || uploading || (!content.trim() && attachments.length === 0)}
          className="bg-[#e8c547] hover:bg-[#d4b340] text-black h-8 w-8 p-0 shrink-0 rounded-lg disabled:opacity-40"
          data-testid="slate-send-btn">
          <Send className="h-3.5 w-3.5" />
        </Button>
      </div>
      <p className="text-[10px] text-[#444] mt-1 ml-1">Enter to send · Shift+Enter new line · # dept · @ person</p>
    </div>
  );
}
